import { ClickAwayListener, FormControl, makeStyles } from '@material-ui/core'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Moment } from 'moment'
import moment from 'moment'
import type { PopperProps as MuiPopperProps } from '@material-ui/core/Popper'
import ClearIcon from '@material-ui/icons/Clear'
import classnames from 'classnames'
import type { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'
import type { CalendarEvent } from 'paintscout'
import IconButton from '@paintscout/ui/components/IconButton'
import Paper from '@paintscout/ui/components/Paper'
import ResponsivePopper from '@paintscout/ui/components/ResponsivePopper'
import InputField from '@paintscout/ui/components/InputField'
import Button from '@paintscout/ui/components/Button'
import { useBreakpoint } from '@paintscout/ui/hooks'
import { useTranslation } from '@paintscout/ui'
import { useDatePickerStyles } from './useDatePickerStyles'
import SingleDatePickerController from './SingleDatePickerController'
import { formatMonthDayYear } from '../../util/formatDate'

/** Height of the `compact` trigger, matching the pill controls it sits beside. */
const COMPACT_HEIGHT = 32

/** Corner radius of the `compact` trigger. */
const COMPACT_RADIUS = 10

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    margin: theme.spacing(4),
    maxWidth: '450px',
    minWidth: '450px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      maxWidth: '100%'
    }
  },
  dialogContent: {},
  disabledInput: {
    cursor: 'default !important',
    '& input': {
      cursor: 'default !important'
    }
  },
  /**
   * `inputVariant="compact"` — a 32px outlined trigger that matches the pill controls in the Quote
   * Settings dialog. Opt-in, so every other date picker in the app keeps the default `InputField`.
   *
   * This renders its own button rather than restyling `InputField`. Overriding `InputField` from the
   * outside does not work: it throws away a caller's `classes` (its own `inputRoot`/`inputInput` keys
   * are declared but never consumed), and `Input`'s root rules for `border-radius` / `border-width`
   * win the cascade even against `!important` descendant selectors. Rendering the trigger directly
   * sidesteps the fight and is the only reliable way to hit the design.
   *
   * `label` / `sublabel` / `error` are ignored in this variant — the caller's own row supplies the
   * label.
   */
  compactTrigger: {
    appearance: 'none',
    width: '100%',
    height: COMPACT_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    boxSizing: 'border-box',
    padding: theme.spacing(0, 1.5),
    background: theme.paintscout.palette.brand.Neutral.White,
    border: `0.8px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: COMPACT_RADIUS,
    font: 'inherit',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 400,
    lineHeight: 1.4,
    color: theme.paintscout.palette.brand.Neutral.P40,
    textAlign: 'left' as const,
    cursor: 'pointer',
    '&:hover': {
      background: theme.paintscout.palette.brand.Neutral.P98
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: theme.paintscout.palette.brand.Blue.Blue55
    }
  },
  compactTriggerDisabled: {
    cursor: 'default',
    background: theme.paintscout.palette.brand.Neutral.P98,
    '&:hover': {
      background: theme.paintscout.palette.brand.Neutral.P98
    }
  },
  compactPlaceholder: {
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  compactValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  }
}))

export interface SingleDatePickerProps {
  classes?: Partial<ReturnType<typeof useStyles>>
  label?: string
  sublabel?: string
  date?: Moment
  disabled?: boolean
  PopperProps?: Partial<MuiPopperProps>
  modalBreakpoint?: keyof BreakpointValues
  clearable?: boolean
  customTrigger?: React.ReactNode
  events?: CalendarEvent[]
  onChange: (date: Moment) => void
  required?: boolean
  /**
   * Chrome for the trigger field. `default` is the standard app field; `compact` is a 32px outlined
   * field that matches the pill controls in the Quote Settings dialog. Opt-in on purpose — pickers
   * elsewhere in the app must keep the default look.
   */
  inputVariant?: 'default' | 'compact'
}

export function SingleDatePicker(props: SingleDatePickerProps) {
  const localClasses = useStyles(props)
  const { __, lang } = useTranslation()
  const {
    label,
    sublabel,
    disabled,
    clearable,
    customTrigger,
    PopperProps,
    modalBreakpoint = 'sm',
    onChange,
    events = [],
    required = false,
    inputVariant = 'default'
  } = props
  const mobile = useBreakpoint((breakpoints) => breakpoints.down(modalBreakpoint))

  const [tableHeight, setTableHeight] = useState(0)

  const anchorRef = useRef(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [date, setDate] = useState<Moment>(props.date)

  useEffect(() => {
    setDate(props.date)
  }, [props.date])

  // useLayoutEffect runs after DOM mutation but before paint, so the measured
  // height is applied before the user sees the fallback (avoids a flicker).
  useLayoutEffect(() => {
    recalculateTableHeight()
  }, [anchorEl])

  const recalculateTableHeight = () => {
    // Scope the query to this picker's subtree so concurrent SingleDatePicker
    // instances (e.g. one inside a dialog stacked over the page) don't measure
    // each other's months. Skip hidden adjacent months (forced to height: 0 by
    // useDatePickerStyles) so we read the actual visible month instead of 0.
    const root = paperRef.current
    if (!root) return
    const visible = root.querySelector<HTMLElement>(
      '.CalendarMonthGrid_month__horizontal:not(.CalendarMonthGrid_month__hidden)'
    )
    if (visible && visible.offsetHeight > 0) {
      setTableHeight(visible.offsetHeight)
    }
  }

  const classes = useDatePickerStyles({ isSingle: true, tableHeight })

  const handleClickAway = (e: React.MouseEvent<Document, MouseEvent>) => {
    // Responsive mode renders the picker as a modal Dialog through a portal.
    // Click-away handling is only appropriate for the desktop Popper; the
    // dialog closes through date selection or its explicit Cancel action.
    if (mobile) {
      return
    }
    if (e.target instanceof HTMLElement && e.target.className.includes('top-navigation')) {
      // This means the click was in a nested dropdown, so we don't want to close the date picker
      return
    }
    setAnchorEl(null)
  }

  const handleChange = (date: Moment) => {
    setDate(date)
    setAnchorEl(null)
    onChange(date)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setDate(null)
    onChange(null)
    setAnchorEl(null)
  }

  const handleTriggerClick = () => {
    setAnchorEl(anchorEl ? null : anchorRef.current)
  }

  const getEventsForDay = (day: Moment) => {
    return events.filter((event) => {
      const start = moment(event.start).startOf('day')
      const end = moment(event.end ?? event.start).endOf('day')
      return day.isBetween(start, end, 'day', '[]')
    })
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl fullWidth>
        {customTrigger ? (
          React.cloneElement(customTrigger as React.ReactElement, {
            onClick: handleTriggerClick,
            ref: anchorRef
          })
        ) : inputVariant === 'compact' ? (
          <button
            type="button"
            ref={anchorRef}
            disabled={disabled}
            onClick={handleTriggerClick}
            className={classnames(localClasses.compactTrigger, {
              [localClasses.compactTriggerDisabled]: disabled
            })}
          >
            <span className={classnames(localClasses.compactValue, { [localClasses.compactPlaceholder]: !date })}>
              {date ? formatMonthDayYear(date.valueOf(), lang) : __('Select Date')}
            </span>
            {clearable && date && (
              <IconButton size="small" onClick={handleClear}>
                <ClearIcon />
              </IconButton>
            )}
          </button>
        ) : (
          <InputField
            required={required}
            className={classnames(classes.input, { [localClasses.disabledInput]: disabled })}
            placeholder={__('Select Date')}
            inputRef={anchorRef}
            readOnly
            fullWidth
            value={date ? formatMonthDayYear(date.valueOf(), lang) : ''}
            label={label}
            sublabel={sublabel}
            onClick={handleTriggerClick}
            disabled={disabled}
            endAdornment={
              clearable &&
              date && (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              )
            }
          />
        )}
        <ResponsivePopper
          classes={{
            root: classes.popper,
            dialogRoot: classnames(classes.popper, localClasses.dialogRoot),
            dialogContent: classnames(classes.dialogContent, localClasses.dialogContent)
          }}
          open={!!anchorEl}
          PopperProps={{
            anchorEl,
            placement: 'bottom-start',
            modifiers: {
              flip: {
                enabled: false
              }
            },
            disablePortal: true,
            ...PopperProps
          }}
          modalBreakpoint={modalBreakpoint}
          footer={
            mobile && (
              <div className={classes.mobileFooter}>
                <Button variant="textNoPadding" onClick={() => setAnchorEl(null)}>
                  {__('Cancel')}
                </Button>
              </div>
            )
          }
        >
          <div ref={paperRef}>
            <Paper className={classes.paper}>
              <SingleDatePickerController
                date={date}
                onChange={handleChange}
                getEventsForDay={getEventsForDay}
                recalculateTableHeight={recalculateTableHeight}
              />
            </Paper>
          </div>
        </ResponsivePopper>
      </FormControl>
    </ClickAwayListener>
  )
}
